import { useRef, useCallback } from 'react';

let sharedCtx: AudioContext | null = null;

const resolveSrc = (raw: string) => {
  const base =
    (import.meta as any)?.env?.BASE_URL ||
    (typeof process !== 'undefined' && (process as any).env?.PUBLIC_URL) ||
    '/';
  if (/^https?:\/\//i.test(raw)) return raw;
  const b = base.endsWith('/') ? base : base + '/';
  return new URL(raw.replace(/^\//, ''), window.location.origin + b).toString();
};

export function useSound(
  rawSrc: string | string[],
  opts?: { timeoutMs?: number; bustCache?: boolean }
) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const list = Array.isArray(rawSrc) ? rawSrc : [rawSrc];
  const timeoutMs = opts?.timeoutMs ?? 5000;

  const ensureAudio = useCallback(() => {
    if (!audioRef.current) {
      const el = new Audio();
      el.preload = 'auto';
      el.setAttribute('playsinline', 'true'); // iOS 必須
      audioRef.current = el;
    }
    return audioRef.current!;
  }, []);

  const play = useCallback(async () => {
    const el = ensureAudio();

    // iOS：需要手勢內解鎖 AudioContext
    try {
      if (!sharedCtx) {
        const Ctx =
          (window as any).AudioContext || (window as any).webkitAudioContext;
        sharedCtx = new Ctx();
      }
      if (sharedCtx.state !== 'running') await sharedCtx.resume().catch(() => {});
    } catch {}

    // 從清單中隨機抽一個檔案
    const raw = list[Math.floor(Math.random() * list.length)];
    const baseSrc = resolveSrc(raw);
    const finalSrc =
      baseSrc +
      ((opts?.bustCache ?? true)
        ? (baseSrc.includes('?') ? '&' : '?') + 'v=' + Date.now()
        : '');

    if (el.src !== finalSrc) {
      el.src = finalSrc;
      el.load();
    }

    // 等待 canplaythrough（有緩衝才播放）
    await new Promise<void>((resolve, reject) => {
      const to = setTimeout(
        () => reject(new Error('Audio load timeout')),
        timeoutMs
      );
      const ok = () => {
        cleanup();
        resolve();
      };
      const bad = () => {
        cleanup();
        reject(new Error('Audio error event'));
      };
      const cleanup = () => {
        clearTimeout(to);
        el.removeEventListener('canplaythrough', ok);
        el.removeEventListener('error', bad);
      };

      if (el.readyState >= 3) {
        clearTimeout(to);
        resolve();
      } else {
        el.addEventListener('canplaythrough', ok, { once: true });
        el.addEventListener('error', bad, { once: true });
      }
    }).catch((e) => {
      console.error('[useSound] load failed', e, {
        src: el.src,
        net: el.networkState,
        ready: el.readyState,
      });
      throw e;
    });

    // 播放，失敗時重試一次
    try {
      el.currentTime = 0;
      await el.play();
    } catch (err) {
      console.warn('[useSound] play failed, retrying…', {
        src: el.src,
        net: el.networkState,
        ready: el.readyState,
      });
      if (sharedCtx && sharedCtx.state !== 'running')
        await sharedCtx.resume().catch(() => {});
      await el.play();
    }
  }, [ensureAudio, list, timeoutMs, opts?.bustCache]);

  return { play, element: audioRef };
}